import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import QuoteModal from "@/components/QuoteModal";

const Products = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const packages = [
    {
      id: 1,
      name: "Pro Custom",
      image: "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/f8djopwd_IMG_3720.JPG",
      description: "Complete customization freedom for your unique team identity",
      features: [
        "Up to 8 custom prints",
        "Fully custom design & colors",
        "Custom color panels, liners, zipper tabs, mesh pockets & more",
        "500+ bags minimum order",
        "90+ days production time"
      ],
      pricing: [
        { quantity: "500+", price: "$55" },
        { quantity: "1000+", price: "$45" }
      ],
      note: "After design approval"
    },
    {
      id: 2,
      name: "Full Custom",
      image: "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/jqbmun7r_IMG_3721.JPG",
      description: "High-level customization with premium features",
      features: [
        "Up to 8 custom prints",
        "Custom liners & zipper tabs",
        "Black bag base",
        "300 bags minimum order",
        "90+ days production time"
      ],
      pricing: [
        { quantity: "Per bag", price: "$65" }
      ],
      note: "After design approval"
    },
    {
      id: 3,
      name: "Semi-Custom | IN STOCK",
      image: "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/gs4suosi_image0%20%2811%29.jpeg",
      description: "Quick turnaround with essential customization",
      features: [
        "2 custom prints included",
        "Up to 5 print locations possible",
        "Black or Navy bag options",
        "No minimum order",
        "3 weeks production time",
        "Perfect for smaller clubs"
      ],
      pricing: [
        { quantity: "Per bag", price: "$75" }
      ],
      note: "After design approval",
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="products-page">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Our Packages
          </h1>
          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Choose the perfect package for your team's needs and budget
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto" data-testid="packages-grid">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col ${
                pkg.highlight ? 'ring-2 ring-gray-900' : ''
              }`}
              data-testid={`package-card-${pkg.id}`}
            >
              {/* Package Image */}
              <div className="relative aspect-square bg-white overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-contain p-8"
                />
                {pkg.highlight && (
                  <div className="absolute top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-bold">
                    POPULAR
                  </div>
                )}
              </div>

              {/* Package Details */}
              <div className="p-8 flex-1 flex flex-col">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {pkg.name}
                </h3>
                <p className="text-gray-600 mb-6 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {pkg.description}
                </p>

                {/* Features */}
                <div className="mb-6 flex-1">
                  <ul className="space-y-3">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <Check className="w-5 h-5 text-gray-900 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pricing */}
                <div className="border-t border-gray-200 pt-6 mb-6">
                  {pkg.pricing.map((price, index) => (
                    <div key={index} className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {price.quantity}
                      </span>
                      <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {price.price}
                      </span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 mt-2 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {pkg.note}
                  </p>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => setShowQuoteModal(true)}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-full transition-all duration-300"
                  data-testid={`request-quote-btn-${pkg.id}`}
                >
                  Request Quote
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 max-w-4xl mx-auto text-center">
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Need Help Choosing?
            </h3>
            <p className="text-gray-700 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Not sure which package is right for your team? Our team is here to help you find the perfect solution for your needs and budget.
            </p>
            <Button
              onClick={() => setShowQuoteModal(true)}
              variant="outline"
              size="lg"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg rounded-full"
              data-testid="contact-us-btn"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal open={showQuoteModal} onClose={() => setShowQuoteModal(false)} />
    </div>
  );
};

export default Products;